/**
 * Shared unwrap helpers for WSDB callable responses.
 *
 * WSDB callables (see WSDB/functions/src/index.ts) always return
 * `{ success: boolean, error?: string, ...payload }`. Both the client-side
 * (services/ChallengeService.js — Firebase Auth transport) and the
 * server-side (functions/services/wsdbClient.js — WS_DB_API_KEY transport)
 * call paths consume that contract, so the unwrap belongs in one place.
 *
 * The transport-specific pieces (which SDK calls the callable, how auth is
 * attached) stay in each caller; only the response shape lives here.
 */

import {z} from "zod";

/**
 * Shape every WSDB callable resolves to: `{ success, error?, ...payload }`.
 * The extra payload keys are domain-specific, so they stay untyped.
 */
export interface WsdbPayload {
  success?: boolean;
  error?: string;
  [key: string]: any;
}

/**
 * zod schema for the WSDB callable envelope. `.passthrough()` keeps the
 * domain-specific payload keys (they vary per callable), so this validates
 * only the contract fields — `success`/`error` — without discarding data.
 * Used for observability at the transport boundary; it does not gate control
 * flow (see `assertWsdbSuccess`, which stays the single source of truth for
 * success/failure).
 */
export const WsdbEnvelopeSchema = z
  .object({
    success: z.boolean().optional(),
    error: z.string().optional()
  })
  .passthrough();

/**
 * Pull the inner payload out of a callable response.
 *
 * Web Firebase SDK returns `{ data: <result> }`; the native shim and
 * server-side firebase/functions wrapper return the result directly. Handle
 * both without forcing callers to remember which transport they're on.
 *
 * @param {unknown} response Raw value the callable returned.
 * @return {Object} The unwrapped payload (the WSDB success/error object).
 */
export const unwrapCallableResponse = (response: any): WsdbPayload => {
  return response?.data ?? response ?? {};
};

/**
 * Validate a WSDB callable payload and throw on failure. Returns the payload
 * unchanged on success so the caller can destructure inline.
 *
 * @param {string} callableName Name of the callable for error messages.
 * @param {Object} payload Unwrapped payload (from `unwrapCallableResponse`).
 * @throws {Error} With the WSDB `error` field as the message when `success`
 *   is not true. The thrown error carries `wsdbResult` for upstream logging.
 * @return {Object} The same payload, narrowed to success shape.
 */
export const assertWsdbSuccess = (callableName: string, payload: WsdbPayload): WsdbPayload => {
  if (!payload?.success) {
    const err: any = new Error(payload?.error || `WSDB callable ${callableName} failed`);
    err.wsdbResult = payload;
    throw err;
  }
  return payload;
};
